import React, { useContext, useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { CampaignProps } from './types/propTypes';
import { useHttp } from 'src/hooks';
import { AuthContext } from 'src/contexts';

const validationSchema = Yup.object().shape({
  campaignName: Yup.string()
    .min(3, 'Campaign name must be at least 3 characters')
    .required('Campaign name is required'),
  accountId: Yup.string().required('Account selection is required'),
  adAccountId: Yup.string().required('Ad account selection is required'),
  facebookPageId: Yup.string().required('Facebook page selection is required'),
  pixelId: Yup.string().required('Pixel selection is required'),
  specialAdCategory: Yup.string().required('Special ad category selection is required'),
  dailyAdBudget: Yup.number()
    .typeError('Daily budget must be a number')
    .min(30, 'Daily budget must be at least $30')
    .positive('Daily budget must be greater than 0')
    .required('Daily budget is required'),
});

const specialAdCategories = [
  { value: 'HOUSING', label: 'Housing' },
  { value: 'FINANCIAL_PRODUCTS_SERVICES', label: 'Financial Products & Services' },
  { value: 'EMPLOYMENT', label: 'Employment' },
  { value: 'ISSUES_ELECTIONS_POLITICS', label: 'Issues, Elections & Politics' },
  { value: 'NONE', label: 'None' },
];

const CampaignNameForm: React.FC<CampaignProps> = ({
  setCampaignData,
  campaignData,
  setIsStepValid,
}) => {
  const { sendRequest } = useHttp();
  const { accessToken } = useContext(AuthContext);

  const [businessAccounts, setBusinessAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [adAccounts, setAdAccounts] = useState([]);
  const [loadingAdAccounts, setLoadingAdAccounts] = useState(false);
  const [pixels, setPixels] = useState([]);
  const [loadingPixels, setLoadingPixels] = useState(false);

  const getBusinessAccounts = () => {
    if (businessAccounts.length > 0) return;
    setLoading(true);

    sendRequest(
      {
        url: `facebook/business-accounts`,
        accessToken,
      },
      data => {
        if (data?.data) {
          setBusinessAccounts(data.data);
        }
        setLoading(false);
      },
    );
  };

  const getAdAccounts = (accountId: string) => {
    if (!accountId) return;

    setLoadingAdAccounts(true);
    sendRequest(
      {
        url: `ads/ad-accounts?businessId=${accountId}`,
        accessToken,
      },
      data => {
        if (data) {
          setAdAccounts(data);
          setLoadingAdAccounts(false);
        }
      },
    );
  };

  const getUserPages = (accountId: string) => {
    if (!accountId) return;
    setPageLoading(true);
    sendRequest(
      {
        url: `facebook/pages?businessId=${accountId}`,
        accessToken,
      },
      data => {
        setPages(data.data || []);
        setPageLoading(false);
      },
    );
  };

  const getPixel = (accountId: string) => {
    if (!accountId) return;
    setLoadingPixels(true);
    sendRequest(
      {
        url: `pixel/${accountId}`,
        accessToken,
      },
      data => {
        setPixels(data?.data || []);
        setLoadingPixels(false);
      },
    );
  };

  return (
    <div className="my-8 p-6 border border-[#E5E7E7] rounded-2xl">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-[28px] text-center font-semibold font-inter text-black mb-6">
          Welcome to the Adly.ai campaign builder
        </h2>

        <Formik
          initialValues={{
            campaignName: campaignData.campaignName,
            accountId: campaignData.accountId,
            adAccountId: campaignData.adAccountId,
            facebookPageId: campaignData.facebookPageId,
            facebookPageName: campaignData.facebookPageName,
            pixelId: campaignData.pixelId,
            specialAdCategory: campaignData.specialAdCategory || 'NONE',
            dailyAdBudget: campaignData.dailyAdBudget,
          }}
          validationSchema={validationSchema}
          validateOnChange={true} // Enable validation on change
          validateOnBlur={true} // Enable validation on blur
        >
          {({ values, handleChange, isValid, dirty, errors, touched, setFieldValue }) => {
            useEffect(() => {
              setCampaignData(prev => ({
                ...prev,
                ...values,
              }));
              
              // Check if form is valid by manually validating all required fields
              const isFormValid = 
                values.campaignName && 
                values.campaignName.length >= 3 &&
                values.accountId &&
                values.adAccountId &&
                values.facebookPageId &&
                values.pixelId &&
                values.specialAdCategory &&
                values.dailyAdBudget &&
                values.dailyAdBudget >= 30 &&
                !errors.dailyAdBudget;
                
              setIsStepValid(isFormValid && dirty);
            }, [values, isValid, dirty, errors]);

            const handleAccountIdChange = e => {
              const selectedAccountId = e.target.value;

              setPages([]);
              setFieldValue('facebookPageId', '');
              setFieldValue('facebookPageName', '');
              setCampaignData(prev => ({
                ...prev,
                accountId: selectedAccountId,
                adAccountId: '',
                facebookPageId: '',
                facebookPageName: '',
              }));

              handleChange(e);
              setAdAccounts([]);
              getAdAccounts(selectedAccountId);
            };

            const handleFacebookPageChange = e => {
              const selectedPageId = e.target.value;
              const selectedPage = pages.find(page => page.id === selectedPageId);

              if (selectedPage) {
                setCampaignData(prev => ({
                  ...prev,
                  facebookPageId: selectedPageId,
                  facebookPageName: selectedPage.name,
                }));
                setFieldValue('facebookPageId', selectedPageId);
                setFieldValue('facebookPageName', selectedPage.name);
              } else {
                setCampaignData(prev => ({
                  ...prev,
                  facebookPageId: '',
                  facebookPageName: '',
                }));
                setFieldValue('facebookPageId', '');
                setFieldValue('facebookPageName', '');
              }
            };

            return (
              <Form className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {/* Campaign Name */}
                <div>
                  <label
                    htmlFor="campaignName"
                    className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                  >
                    Name of your campaign <span className="text-[#CC0E3C]">*</span>
                  </label>
                  <Field
                    type="text"
                    name="campaignName"
                    placeholder="Enter campaign name"
                    className="w-full px-4 py-2 border rounded-lg text-base font-inter font-normal outline-none"
                  />
                  <ErrorMessage
                    name="campaignName"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Account Selection */}
                <div>
                  <label
                    htmlFor="accountId"
                    className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                  >
                    Account <span className="text-[#CC0E3C]">*</span>
                  </label>
                  <Field
                    as="select"
                    name="accountId"
                    className="w-full px-4 py-2 border rounded-lg bg-white text-base font-inter font-normal outline-none"
                    onFocus={getBusinessAccounts}
                    onChange={handleAccountIdChange}
                  >
                    <option value="">Select account</option>
                    {loading ? (
                      <option disabled>Loading...</option>
                    ) : (
                      businessAccounts?.map(account => (
                        <option key={account?.id} value={account?.id}>
                          {account?.name}
                        </option>
                      ))
                    )}
                  </Field>
                  <ErrorMessage
                    name="accountId"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Only show Ad Account dropdown when Business Account is selected */}
                {values.accountId && (
                  <div>
                    <label
                      htmlFor="adAccountId"
                      className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                    >
                      Ad account <span className="text-[#CC0E3C]">*</span>
                    </label>
                    <Field
                      as="select"
                      name="adAccountId"
                      className="w-full px-4 py-2 border rounded-lg bg-white text-base font-inter font-normal outline-none"
                      disabled={!values.accountId}
                    >
                      <option value="">Select ad account</option>
                      {loadingAdAccounts ? (
                        <option disabled>Loading...</option>
                      ) : (
                        adAccounts?.map(adAccount => (
                          <option key={adAccount?.id} value={adAccount.account_id}>
                            {adAccount?.name}
                          </option>
                        ))
                      )}
                    </Field>
                    <ErrorMessage
                      name="adAccountId"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                )}

                {/* Facebook Page */}
                {values.accountId && (
                  <div>
                    <label
                      htmlFor="facebookPageId"
                      className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                    >
                      Facebook page <span className="text-[#CC0E3C]">*</span>
                    </label>
                    <Field
                      as="select"
                      name="facebookPageId"
                      className="w-full px-4 py-2 border rounded-lg bg-white text-base font-inter font-normal outline-none"
                      onFocus={() => getUserPages(values.accountId)}
                      onChange={handleFacebookPageChange}
                    >
                      <option value="">Select Facebook page</option>
                      {pageLoading ? (
                        <option disabled>Loading...</option>
                      ) : (
                        pages?.map(page => (
                          <option key={page?.id} value={page?.id}>
                            {page?.name}
                          </option>
                        ))
                      )}
                    </Field>
                    <ErrorMessage
                      name="facebookPageId"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                )}

                {/* Pixel */}
                {values.adAccountId && (
                  <div>
                    <label
                      htmlFor="pixelId"
                      className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                    >
                      Pixel <span className="text-[#CC0E3C]">*</span>
                    </label>
                    <Field
                      as="select"
                      name="pixelId"
                      className="w-full px-4 py-2 border rounded-lg bg-white text-base font-inter font-normal outline-none"
                      onFocus={() => getPixel(values.adAccountId)}
                    >
                      <option value="">Select pixel</option>
                      {loadingPixels ? (
                        <option disabled>Loading...</option>
                      ) : (
                        pixels?.map(pixel => (
                          <option key={pixel?.id} value={pixel?.id}>
                            {pixel?.name}
                          </option>
                        ))
                      )}
                    </Field>
                    <ErrorMessage
                      name="pixelId"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                )}

                {/* Special Ad Category */}
                <div>
                  <label
                    htmlFor="specialAdCategory"
                    className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                  >
                    Special ad category <span className="text-[#CC0E3C]">*</span>
                  </label>
                  <Field
                    as="select"
                    name="specialAdCategory"
                    className="w-full px-4 py-2 border rounded-lg bg-white text-base font-inter font-normal outline-none"
                  >
                    {specialAdCategories?.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </Field>

                  <ErrorMessage
                    name="specialAdCategory"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>

                {/* Daily Budget */}
                <div className="md:col-span-2 flex items-center gap-x-4">
                  <div className="flex-grow">
                    <label
                      htmlFor="dailyAdBudget"
                      className="block text-[#4A4D4F] text-base font-normal font-inter mb-2"
                    >
                      What is your daily ad budget? <span className="text-[#CC0E3C]">*</span>
                    </label>
                    <div className="relative">
                      <Field
                        type="number"
                        name="dailyAdBudget"
                        placeholder="$30"
                        className="w-full px-4 py-2 border rounded-lg text-base font-inter font-normal outline-none pr-12"
                        min="30"
                        onKeyPress={e => {
                          if (e.key === '-' || e.key === '+') {
                            e.preventDefault();
                          }
                        }}
                      />
                      <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-base font-inter font-semibold text-[#4A4D4F]">
                        USD
                      </span>
                    </div>
                    <ErrorMessage
                      name="dailyAdBudget"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                    {values.dailyAdBudget && values.dailyAdBudget < 30 && (
                      <div className="text-red-500 text-sm mt-1">
                        Daily budget must be at least $30
                      </div>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default CampaignNameForm;